interface AdminAuthResponse {
  success: boolean;
  sessionToken?: string;
  message?: string;
  error?: string;
}

const ADMIN_SESSION_KEY = 'admin_session_token';

/**
 * Authenticate admin with hardcoded credentials
 */
export async function authenticateAdmin(username: string, password: string): Promise<AdminAuthResponse> {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ username, password })
    });

    const data = await response.json();

    if (response.ok && data.success) {
      // Store session token
      sessionStorage.setItem(ADMIN_SESSION_KEY, data.sessionToken);
      return data;
    } else {
      return { success: false, error: data.error || 'Authentication failed' };
    }
  } catch (error) {
    return { success: false, error: 'Network error during authentication' };
  }
}

/**
 * Check if admin is currently authenticated
 */
export function isAdminAuthenticated(): boolean {
  const token = sessionStorage.getItem(ADMIN_SESSION_KEY);
  return token !== null && token.startsWith('admin_');
}

/**
 * Get current admin session token
 */
export function getAdminSessionToken(): string | null {
  return sessionStorage.getItem(ADMIN_SESSION_KEY);
}

/**
 * Clear admin session (logout)
 */
export function clearAdminSession(): void {
  sessionStorage.removeItem(ADMIN_SESSION_KEY);
}

/**
 * Get admin API token for secured edge functions
 */
export function getAdminApiToken(): string | null {
  const sessionToken = sessionStorage.getItem(ADMIN_SESSION_KEY);
  if (!sessionToken || !sessionToken.startsWith('admin_')) {
    return null;
  }
  
  // For now, we'll use the session token as the API token
  // In a production system, you'd want to exchange this for a proper API token
  return sessionToken;
}

/**
 * Admin authentication context
 */
export const adminAuth = {
  authenticate: authenticateAdmin,
  isAuthenticated: isAdminAuthenticated,
  getToken: getAdminSessionToken,
  getApiToken: getAdminApiToken,
  logout: clearAdminSession
}; 